/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AppDispatch, RootState } from 'redux/store';
import PrimaryModal from 'components/common/PrimaryModal/PrimaryModal';
import styled from '@emotion/styled';
import { TextStyle } from 'styles/variables/mixins';
import { Box, TextField, MenuItem, Grid, Alert } from '@mui/material';
import { RichTextEditor } from 'views/retainerAgreement/components/editor/RichTextEditor';
import { VariablesPanel } from 'views/retainerAgreement/components/editor/VariablesPanel';
import {
    updateTemplate,
    loadTemplatesForSelection,
} from 'redux/retainer-templates/templates.middleware';
import {
    setShowInlineEditor,
    setTemplateBeingEdited,
} from 'redux/retainer-templates/templates.slice';
import { useToast } from 'hooks/use-toast';
import { isFeatureNotEnabledError } from 'utils/features';
import { Template } from './TemplateDropdownWithActions';
import InfoIcon from '@mui/icons-material/Info';
import type { TemplateType } from 'types';

interface InlineTemplateEditorProps {
    open: boolean;
    template: Template | null;
    onClose: () => void;
    onSuccess?: () => void;
    mobile?: boolean;
}

const SaveButton = styled.button<{ mobile?: boolean; disabled?: boolean }>`
    flex: 1 1 0;
    width: 180px;
    height: 36px;
    padding: 0 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    ${TextStyle({ fontSize: '14px', fontWeight: '400', color: '#8353E2FF' })}
    background: white;
    opacity: ${(props) => (props.disabled ? 0.5 : 1)};
    border: 1px solid #8353e2ff;
    border-radius: 3px;
    cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};

    &:hover {
        color: ${(props) => (props.disabled ? '#8353E2FF' : '#ffffffff')};
        background: ${(props) => (props.disabled ? 'white' : '#6025d8ff')};
    }

    width: ${(props) => (props.mobile ? '137px !important' : '180px')};
`;

const Buttons = styled.div`
    margin: auto;
    margin-top: 30px;
    display: flex;
    justify-content: space-around;
    gap: 22px;
`;

const FormSection = styled(Box)`
    margin-bottom: 24px;
`;

const EditorContainer = styled(Box)`
    display: flex;
    gap: 16px;
    margin-top: 16px;
    height: 400px;
`;

const EditorWrapper = styled(Box)`
    flex: 1;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

const VariablesWrapper = styled(Box)`
    width: 280px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    overflow: hidden;
`;

const Title = styled.h3`
    text-align: center;
    font-weight: bold;
    color: #000;
    margin-bottom: 20px;
`;

const InlineTemplateEditor = ({
    open,
    template,
    onClose,
    onSuccess,
    mobile,
}: InlineTemplateEditorProps) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { isLoading } = useSelector((state: RootState) => state.templates);
    const { t } = useTranslation();
    const { toast } = useToast();
    const dispatch = useDispatch<AppDispatch>();

    const [templateName, setTemplateName] = useState('');
    const [templateContent, setTemplateContent] = useState('');
    const [country, setCountry] = useState('');
    const [program, setProgram] = useState('');
    const [templateType, setTemplateType] = useState<TemplateType>('visa-application');

    // Editor instance for inserting placeholders
    const [editorInstance] = useState<any>(null);

    const canSave = templateName.trim().length > 0 && templateContent.trim().length > 0;

    useEffect(() => {
        // Load template data when modal opens
        if (open && template) {
            setTemplateName(template.name || '');
            setTemplateContent((template as any).content || '<p>Edit your template...</p>');
            setCountry(template.country || '');
            setProgram(template.program || '');
            setTemplateType((template.type as TemplateType) || 'visa-application');
        }
    }, [open, template]);

    const handleInsertPlaceholder = (token: string, mandatory: boolean) => {
        if (editorInstance) {
            // Insert placeholder at cursor position
            editorInstance.chain().focus().insertContent(token).run();
        }
    };

    const handleSave = async () => {
        if (!canSave || !template?.id) return;

        try {
            const payload = {
                name: templateName,
                content: templateContent,
                type: templateType,
                country: country || undefined,
                program: program || undefined,
            };

            const result = await dispatch(
                updateTemplate({ id: Number(template.id), updates: payload })
            );

            if (updateTemplate.fulfilled.match(result)) {
                toast({
                    title: t('success', { lng: language }) || 'Success',
                    description:
                        t('templateUpdatedSuccessfully', { lng: language }) ||
                        'Template updated successfully',
                });

                // Refresh template list for selection
                await dispatch(loadTemplatesForSelection({}));

                onSuccess?.();

                // Close modal
                dispatch(setShowInlineEditor(false));
                dispatch(setTemplateBeingEdited(null));
                onClose();
            } else {
                if (isFeatureNotEnabledError(result.payload)) {
                    return;
                }
                toast({
                    title: t('error', { lng: language }) || 'Error',
                    description:
                        t('failedToUpdateTemplate', { lng: language }) ||
                        'Failed to update template',
                    variant: 'destructive',
                });
            }
        } catch (error) {
            if (isFeatureNotEnabledError(error)) {
                return;
            }
            console.error('Error updating template:', error);
            toast({
                title: t('error', { lng: language }) || 'Error',
                description:
                    t('failedToUpdateTemplate', { lng: language }) || 'Failed to update template',
                variant: 'destructive',
            });
        }
    };

    const handleCancel = () => {
        dispatch(setShowInlineEditor(false));
        dispatch(setTemplateBeingEdited(null));
        onClose();
    };

    return (
        <PrimaryModal
            style={{
                top: '50%',
                transform: 'translate(-50%, -50%)',
                padding: '24px',
                borderRadius: '10px',
                maxHeight: '90vh',
                overflow: 'auto',
            }}
            maxWidth="1000px"
            open={open}
            onClose={onClose}
        >
            <Box>
                <Title>{t('editTemplate', { lng: language }) || 'Edit Template'}</Title>

                <Alert severity="info" icon={<InfoIcon />} sx={{ marginBottom: 2 }}>
                    {t('editingAffectsOnlyFutureAgreements', { lng: language }) ||
                        'Editing affects only future agreements. Existing agreements remain unchanged.'}
                </Alert>

                <FormSection>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                required
                                label={t('templateName', { lng: language }) || 'Template Name'}
                                value={templateName}
                                onChange={(e) => setTemplateName(e.target.value)}
                                variant="outlined"
                                size="small"
                                placeholder="e.g., Australia PR Retainer"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                select
                                label={t('templateType', { lng: language }) || 'Template Type'}
                                value={templateType}
                                onChange={(e) => setTemplateType(e.target.value as TemplateType)}
                                variant="outlined"
                                size="small"
                            >
                                <MenuItem value="visa-application">Visa Application</MenuItem>
                                <MenuItem value="eligibility">Eligibility</MenuItem>
                                <MenuItem value="service-agreement">Service Agreement</MenuItem>
                                <MenuItem value="milestones">Milestones</MenuItem>
                                <MenuItem value="engagement">Engagement</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label={t('country', { lng: language }) || 'Country (Optional)'}
                                value={country}
                                onChange={(e) => setCountry(e.target.value)}
                                variant="outlined"
                                size="small"
                                placeholder="e.g., Australia"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label={t('program', { lng: language }) || 'Program (Optional)'}
                                value={program}
                                onChange={(e) => setProgram(e.target.value)}
                                variant="outlined"
                                size="small"
                                placeholder="e.g., Skilled Independent"
                            />
                        </Grid>
                    </Grid>
                </FormSection>

                <EditorContainer>
                    <EditorWrapper>
                        <RichTextEditor
                            content={templateContent}
                            onChange={(html: string) => {
                                setTemplateContent(html);
                            }}
                            editable={true}
                            templateType={templateType}
                        />
                    </EditorWrapper>
                    <VariablesWrapper>
                        <VariablesPanel
                            onInsert={handleInsertPlaceholder}
                            templateType={templateType}
                        />
                    </VariablesWrapper>
                </EditorContainer>

                <Buttons>
                    <SaveButton onClick={handleCancel} mobile={mobile} disabled={isLoading}>
                        {t('cancel', { lng: language }) || 'Cancel'}
                    </SaveButton>

                    <SaveButton
                        onClick={handleSave}
                        mobile={mobile}
                        disabled={!canSave || isLoading}
                    >
                        {isLoading
                            ? t('saving', { lng: language }) || 'Saving...'
                            : t('saveChanges', { lng: language }) || 'Save Changes'}
                    </SaveButton>
                </Buttons>
            </Box>
        </PrimaryModal>
    );
};

export default InlineTemplateEditor;
